import React, { Fragment, useEffect } from "react";
import { useDropzone } from "react-dropzone";

import { func, string, bool, object } from "prop-types";
import loadImage from "blueimp-load-image";
import { CvFileIcon } from "../../components/EditCv";

import { canvasToBase64 } from "../../helpers/utils";
import * as style from "./styles.module.scss";
import { isSmallerThanDesktop, getArticle } from "../../helpers";
import { useWindowDimensions } from "../../helpers/hooks";

const AwaitingDrop = ({ additionalMessage, fileTypeHint, dropFileMsg }) => (
  <div className="d-flex flex-column align-items-center mx-4">
    <p className={style.dropzone__title}>{dropFileMsg}</p>
    {additionalMessage && (
      <p className={style.dropzone__subtitle}>{additionalMessage}</p>
    )}
    <p className={style.dropzone__subtitle}>{fileTypeHint}</p>
  </div>
);

AwaitingDrop.propTypes = {
  dropFileMsg: string.isRequired,
  fileTypeHint: string.isRequired,
  additionalMessage: string,
};

const DroppedWrongFile = ({ wrongFileTypeHint, alternativeMessage }) => (
  <div className="d-flex flex-column align-items-center">
    <CvFileIcon isBig text={"!"} />
    <p className={style.dropzone__title}>Wrong file type</p>
    {alternativeMessage ? (
      <p className={style.dropzone__subtitle}>{alternativeMessage}</p>
    ) : (
      <Fragment>
        <p className={style.dropzone__subtitle}>{wrongFileTypeHint}</p>
        <p className={style.dropzone__subtitle}>
          Drop another file here to try again
        </p>
      </Fragment>
    )}
  </div>
);
DroppedWrongFile.propTypes = {
  wrongFileTypeHint: string.isRequired,
  alternativeMessage: string,
};

const DroppedImageSuccess = ({
  image,
  imageMaxHeight,
  imageMaxWidth,
  isMobile,
  url,
}) => {
  if (!image && !url) {
    return null;
  }
  const getImageWidth = () => {
    if (imageMaxWidth) return imageMaxWidth;
    return isMobile ? "130px" : "500px";
  };
  return (
    <img
      className="d-flex flex-column align-items-center mx-4 mb-1 w-100 h-100"
      src={image ? `data:image/jpeg;base64,${image}` : url}
      style={{
        objectFit: "contain",
        maxWidth: getImageWidth(),
        maxHeight: imageMaxHeight,
      }}
    />
  );
};

DroppedImageSuccess.propTypes = {
  image: string.isRequired,
  isMobile: bool.isRequired,
  imageMaxHeight: string,
  imageMaxWidth: string,
  url: string,
};

const FileDropzone = ({
  fileTypeHint,
  additionalMessage,
  acceptedFileTypes,
  handleChange,
  fileStateName,
  fileParsedToString,
  wrongFileTypeHint,
  dropFileMsg,
  handleInvalidType,
  isFileValid,
  fileCategory,
  width = "700px",
  height = "400px",
  imageMaxWidth,
  imageUploadOptions = {},
  url,
}) => {
  const {
    getRootProps,
    getInputProps,
    open,
    acceptedFiles,
    fileRejections,
    isDragActive,
  } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: acceptedFileTypes,
  });

  useEffect(() => {
    if (acceptedFiles[0]) {
      loadImage(
        acceptedFiles[0],
        canvasToBase64((base64Str) => {
          handleChange(fileStateName, base64Str);
        }),
        {
          maxWidth: 800,
          maxHeight: 800,
          contain: true,
          canvas: true,
          orientation: true,
          meta: true,
          ...imageUploadOptions,
        }
      );
    }
    if (fileRejections[0]) {
      if (handleInvalidType) {
        handleInvalidType();
      }
      handleChange(fileStateName, null);
    }
  }, [acceptedFiles, fileRejections]);

  const isWrongFileType =
    !fileParsedToString && (!isFileValid || fileRejections.length > 0);
  const isFileDropped = fileParsedToString || url || acceptedFiles.length > 0;
  const isAwaitingDrop = !isFileDropped && !isWrongFileType;

  const { windowWidth } = useWindowDimensions();
  const isSmallerThanLarge = isSmallerThanDesktop(windowWidth);

  const calculateImageHeight = () => {
    const dropzoneHeight = height.includes("px")
      ? height.substr(0, height.indexOf("px"))
      : "80%";
    return `${Number(dropzoneHeight) - 90}px`;
  };

  return (
    <div
      {...getRootProps({ className: "dropzone" })}
      className={`p-1 ${style.dropzone} 
      ${isFileDropped || isDragActive ? style.dropzone_success : ""}
      ${isSmallerThanLarge && isAwaitingDrop ? style.dropzone__invisible : ""}`}
      style={{
        width: `${isSmallerThanLarge ? "100%" : width}`,
        height: `${isSmallerThanLarge ? "100%" : height}`,
      }}
    >
      <input {...getInputProps()} />

      {isAwaitingDrop && !isSmallerThanLarge && (
        <AwaitingDrop
          fileTypeHint={fileTypeHint}
          additionalMessage={additionalMessage}
          dropFileMsg={dropFileMsg}
        />
      )}
      {isWrongFileType && (
        <DroppedWrongFile
          wrongFileTypeHint={wrongFileTypeHint}
          alternativeMessage={isSmallerThanLarge ? fileTypeHint : null}
        />
      )}
      {isFileDropped && (
        <DroppedImageSuccess
          image={fileParsedToString}
          isMobile={isSmallerThanLarge}
          imageMaxHeight={calculateImageHeight()}
          imageMaxWidth={imageMaxWidth}
          url={url}
        />
      )}
      {!isFileDropped && !isSmallerThanLarge && (
        <div className="d-flex align-items-center my-1 justify-content-center w-100">
          <span className={style.dropzone__line} />
          <span className="px-1 font-weight-bold">or</span>
          <span className={style.dropzone__line} />
        </div>
      )}
      {(!isSmallerThanLarge || (isSmallerThanLarge && isFileDropped)) && (
        <button
          className={`btn btn-outline-info text-dark ${style.dropzone__btn}`}
          onClick={open}
        >
          Choose{" "}
          {isFileDropped || isWrongFileType
            ? "a different"
            : getArticle(fileCategory)}{" "}
          {fileCategory}
        </button>
      )}
    </div>
  );
};

FileDropzone.propTypes = {
  fileTypeHint: string.isRequired,
  wrongFileTypeHint: string.isRequired,
  acceptedFileTypes: string.isRequired,
  additionalMessage: string,
  handleChange: func,
  fileParsedToString: string,
  width: string,
  height: string,
  handleInvalidType: func,
  isFileValid: bool,
  fileCategory: string.isRequired,
  fileStateName: string.isRequired,
  dropFileMsg: string.isRequired,
  imageMaxWidth: string,
  imageUploadOptions: object,
  url: string,
};

export default FileDropzone;
